import { format } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { MdDelete } from 'react-icons/md';
import { CellProps, Column, } from 'react-table';
import { Checkbox } from 'reakit';
import {
  IHour,
  cover24Hours,
  parseDateOnlyString,
  parseTimeOnly,
  prettyTimeOnly
} from '@gettactic/api';
import { classNames } from '@/lib/classNames';
import {
  SearchInput,
  TableCellContentRenderer,
  TableHeaderContentRenderer
} from '@/components/basic';
import {
  ConfirmOperationModal,
  IHourWithResName,
  groupSharedKey
} from '@/components/hours/rule/ListHoursRules';
import { HourRulesTableView } from './HourRulesTableView';

export interface SharedHourRulesTableProps {
  loading: boolean;
  data: IHourWithResName[];
  setOperationalModal: React.Dispatch<ConfirmOperationModal | null>;
  invalidateQuery: () => Promise<void>;
}
type CellPropsOnIHour<T> = CellProps<IHour, T>;

export function NonSharedHourRulesTable({
  data: rawData,
  setOperationalModal,
  invalidateQuery
}: SharedHourRulesTableProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [bulk, setBulk] = useState<string[]>([]);
  const data = useMemo(() => {
    const q = query.toLowerCase().trim();
    return q.length
      ? rawData.filter((x) => x.name?.toLowerCase().includes(q))
      : rawData;
  }, [query, rawData]);
  const selectedAll = data.length ? data.length === bulk.length : false;
  const visibleSelected = data.filter(
    (x) => bulk.indexOf(groupSharedKey(x)) > -1
  );

  const setDeleteModal = (h: IHour | null) => {
    if (h) {
      setOperationalModal({ operation: 'delete', applyTo: [h] });
    }
  };
  const columns: Column<IHourWithResName>[] = [
    {
      Header() {
        return (
          <TableHeaderContentRenderer>
            <div className="px-2">
              <Checkbox
                className="rounded text-primary focus:ring-0"
                checked={selectedAll}
                onClick={() => {
                  if (!selectedAll) {
                    setBulk((x) => data.map((x) => groupSharedKey(x)));
                  } else {
                    setBulk([]);
                  }
                }}
              />
            </div>
          </TableHeaderContentRenderer>
        );
      },
      accessor: undefined,
      id: 'selected',
      Cell({ row: { original: hour } }: CellPropsOnIHour<"selected">) {
        const selected = bulk.indexOf(groupSharedKey(hour)) > -1;
        return (
          <div className="flex items-center justify-start space-x-2 px-2">
            <Checkbox
              className="rounded text-primary focus:ring-0"
              checked={selected}
              onClick={() => {
                if (selected) {
                  setBulk((x) => x.filter((s) => s !== groupSharedKey(hour)));
                } else {
                  setBulk((x) => [...x, groupSharedKey(hour)]);
                }
              }}
            />
          </div>
        );
      }
    },
    {
      Header() {
        return (
          <TableHeaderContentRenderer>
            <div className="">Name</div>
          </TableHeaderContentRenderer>
        );
      },
      accessor: 'name',
      Cell: ({ value }: CellPropsOnIHour<"name">) => (
        <div
          title={value ?? undefined}
          className="w-10 overflow-hidden overflow-ellipsis text-sm sm:w-14 md:w-14"
        >
          {value}
        </div>
      )
    },
    {
      Header() {
        return (
          <TableHeaderContentRenderer>
            <div className="">Resource</div>
          </TableHeaderContentRenderer>
        );
      },
      accessor: 'resName',
      Cell: ({ value }: CellPropsOnIHour<"resName">) => (
        <div
          title={value ?? undefined}
          className="w-10 overflow-hidden overflow-ellipsis text-sm sm:w-14 md:w-14"
        >
          {value}
        </div>
      )
    },
    {
      Header() {
        return (
          <TableHeaderContentRenderer>
            <div>Dates</div>
          </TableHeaderContentRenderer>
        );
      },
      accessor: undefined,
      id: 'dates',
      Cell({ row: { original: hour } }: CellPropsOnIHour<"dates">) {
        const since = hour.since ? parseDateOnlyString(hour.since) : null;
        const until = hour.until ? parseDateOnlyString(hour.until) : null;
        return (
          <div className="text-sm">
            {!since && !until ? (
              <span className="font-bold">Always</span>
            ) : (
              <>
                <span>{since ? format(since, 'M/dd/yy') : null}</span>
                <span>-</span>
                {until ? <div>{format(until, 'M/dd/yy')}</div> : null}
              </>
            )}
          </div>
        );
      }
    },
    {
      Header() {
        return (
          <TableHeaderContentRenderer>
            <div>Hours</div>
          </TableHeaderContentRenderer>
        );
      },
      accessor: undefined,
      id: 'hours',
      Cell({ row: { original: hour } }: CellPropsOnIHour<"hours">) {
        return (
          <div className="text-sm">
            {cover24Hours(
              parseTimeOnly(hour.start_time),
              parseTimeOnly(hour.end_time)
            ) ? (
              <div className="">All day</div>
            ) : (
              <div className="w-16">
                <span>{prettyTimeOnly(hour.start_time)}</span>
                <span>{prettyTimeOnly(hour.end_time)}</span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      Header() {
        return (
          <TableHeaderContentRenderer>
            <div title={'Availability'} className="">
              Availa.
            </div>
          </TableHeaderContentRenderer>
        );
      },
      accessor: undefined,
      id: 'visibility',
      Cell({ row: { original: hour } }: CellPropsOnIHour<"visibility">) {
        return (
          <div className="text-center text-sm">
            {hour.is_available ? (
              <div className="w-16 rounded-full bg-green-600 px-1 py-1 text-white">
                Open
              </div>
            ) : (
              <div className="w-16 rounded-full bg-red-600 px-1 py-1  text-white">
                Closed
              </div>
            )}
          </div>
        );
      }
    },
    {
      Header: '',
      id: 'edit-menu-placeholder',
      accessor: undefined,
      Cell: ({ row }: CellPropsOnIHour<"edit-menu-placeholder">) =>
        TableCellContentRenderer.EditDeleteControls({
          row,
          setDeleteModal,
          setEditModal: (h) => {
            if (h) {
              setOperationalModal({ operation: 'edit', applyTo: [h] });
            }
          }
        })
    }
  ];

  return (
    <div>
      <div className="flex items-center justify-between gap-16">
        <SearchInput query={query} setQuery={setQuery} />
        <div className="flex flex-nowrap">
          {visibleSelected.length ? (
            <p>Selected {visibleSelected.length}</p>
          ) : null}
          <button
            disabled={bulk.length === 0}
            onClick={(ev) => {
              ev.preventDefault();
              const deleteAll: IHour[] = [];
              visibleSelected.forEach((x) => deleteAll.push(x));
              setOperationalModal({ operation: 'delete', applyTo: deleteAll });
            }}
            className={classNames(
              visibleSelected.length
                ? 'text-gray-600 hover:text-gray-800'
                : 'text-gray-100',
              'px-2'
            )}
          >
            <MdDelete className="h-5 w-5" />
          </button>
        </div>
      </div>
      <HourRulesTableView
        reorderable
        columns={columns}
        list={data}
        invalidateQuery={invalidateQuery}
        updateFunction={null}
      />
    </div>
  );
}
